/**
 * Outil « Banc d'optique » (Boîte à outils, physique-chimie 5e → Tle) — logique PURE.
 *
 * Quatre parties : ombres (source ponctuelle, objet opaque, écran : ombre propre, ombre portée,
 * taille par le théorème de Thalès), réfraction (lois de Snell-Descartes, réflexion totale),
 * lentille mince convergente (construction de l'image, grandissement, relation de conjugaison)
 * et lunette astronomique afocale (faisceau, grossissement). Positions algébriques sur l'axe
 * optique orienté dans le sens de la lumière, origine au centre optique O (comme les cartes).
 */

import {
  frNum,
  mcqOptions,
  pick,
  retryMcq,
  sigFixed,
  sigNum,
  type Mcq,
  type Rng,
} from '../../ui/mcq';

export const fr = frNum;
export const sig = sigNum;
const NB = String.fromCharCode(0xa0);

export type Part = 'shadow' | 'refraction' | 'lens' | 'telescope';

export const PARTS: ReadonlyArray<{ id: Part; label: string; minRank: number; maxRank: number }> = [
  { id: 'shadow', label: 'Ombres', minRank: 1, maxRank: 4 },
  { id: 'refraction', label: 'Réfraction', minRank: 5, maxRank: 7 },
  { id: 'lens', label: 'Lentille', minRank: 5, maxRank: 7 },
  { id: 'telescope', label: 'Lunette', minRank: 7, maxRank: 7 },
];

export function partsFor(rank: number | null) {
  return PARTS.filter((p) => rank === null || (rank >= p.minRank && rank <= p.maxRank));
}

/* ---------- ombres ---------- */

export interface ShadowInput {
  /** distance source – objet (cm) */
  d1: number;
  /** distance source – écran (cm) */
  d2: number;
  /** hauteur de l'objet (cm) */
  h: number;
}

/** hauteur de l'ombre portée : H = h × d2 / d1 (Thalès, source ponctuelle sur l'axe) */
export const shadowHeight = (s: ShadowInput) => (s.h * s.d2) / s.d1;

/* ---------- réfraction ---------- */

export const MEDIA = [
  { id: 'air', label: 'Air', n: 1.0 },
  { id: 'water', label: 'Eau', n: 1.33 },
  { id: 'glass', label: 'Verre', n: 1.5 },
  { id: 'diamond', label: 'Diamant', n: 2.42 },
] as const;
export type MediumId = (typeof MEDIA)[number]['id'];
export const nOf = (id: MediumId) => MEDIA.find((m) => m.id === id)!.n;

const RAD = Math.PI / 180;

/** angle de réfraction (°) ; null : réflexion totale */
export function refract(n1: number, n2: number, i1: number): number | null {
  const s = (n1 * Math.sin(i1 * RAD)) / n2;
  if (s > 1) return null;
  return Math.asin(s) / RAD;
}

/** angle limite (°) quand n1 > n2, sinon null */
export function criticalAngle(n1: number, n2: number): number | null {
  return n1 > n2 ? Math.asin(n2 / n1) / RAD : null;
}

/* ---------- lentille ---------- */

export interface LensInput {
  /** distance focale f' (cm) */
  f: number;
  /** position algébrique de l'objet OA (cm, < 0 : objet réel) */
  OA: number;
  /** taille algébrique de l'objet AB (cm) */
  AB: number;
}

export interface LensImage {
  /** OA' (cm) ; Infinity : image à l'infini */
  OA: number;
  AB: number;
  gamma: number;
  /** réelle (après la lentille) ou virtuelle (du même côté que l'objet) */
  kind: 'real' | 'virtual' | 'infinite';
}

/** relation de conjugaison 1/OA' − 1/OA = 1/f' ; γ = OA'/OA */
export function lensImage({ f, OA, AB }: LensInput): LensImage {
  const inv = 1 / f + 1 / OA;
  if (Math.abs(inv) < 1e-12)
    return { OA: Infinity, AB: Infinity, gamma: Infinity, kind: 'infinite' };
  const OAp = 1 / inv;
  const gamma = OAp / OA;
  return { OA: OAp, AB: gamma * AB, gamma, kind: OAp > 0 ? 'real' : 'virtual' };
}

/* ---------- lunette afocale ---------- */

export interface TelescopeInput {
  /** focales de l'objectif et de l'oculaire (mm) */
  f1: number;
  f2: number;
  /** angle sous lequel on voit l'astre à l'œil nu (rad) */
  alpha: number;
}

export function telescope({ f1, f2, alpha }: TelescopeInput) {
  const G = f1 / f2;
  return { G, alphaP: G * alpha, A1B1: f1 * Math.tan(alpha), length: f1 + f2 };
}

/* ---------- écriture ---------- */

export const cm = (v: number) => (Number.isFinite(v) ? `${sig(v)}${NB}cm` : '∞');
export const deg = (v: number) => `${fr(v, 1)}°`;
const signed = (v: number) => (v > 0 ? `+${sig(v)}` : sig(v));

/* ---------- phrases du prof ---------- */

export function shadowLines(s: ShadowInput, rank: number | null): string[] {
  const H = shadowHeight(s);
  const out = [
    'La lumière se propage en ligne droite : les rayons qui frôlent les bords de l’objet limitent l’ombre portée.',
    'Ombre propre : la face de l’objet tournée vers l’écran, non éclairée. Ombre portée : la zone sombre sur l’écran.',
  ];
  out.push(
    rank !== null && rank <= 2
      ? `L’ombre portée mesure ${cm(H)} : ${H > s.h ? 'plus grande' : 'à peine plus grande'} que l’objet (${cm(s.h)}). Rapproche l’objet de la lampe : elle grandit.`
      : `Thalès : H = h × d₂ ÷ d₁ = ${fr(s.h, 1)} × ${fr(s.d2, 0)} ÷ ${fr(s.d1, 0)} ≈ ${cm(H)}.`,
  );
  return out;
}

export function refractionLines(n1: number, n2: number, i1: number): string[] {
  const i2 = refract(n1, n2, i1);
  const lim = criticalAngle(n1, n2);
  const out = [`Réflexion : l’angle de réflexion est égal à l’angle d’incidence, ${deg(i1)}.`];
  if (i2 === null) {
    out.push(
      `n₁ sin i₁ = ${fr(n1, 2)} × sin ${fr(i1, 0)}° ≈ ${fr(n1 * Math.sin(i1 * RAD), 2)} > n₂ = ${fr(n2, 2)} : pas de rayon réfracté, toute la lumière est réfléchie (réflexion totale, au-delà de ${deg(lim!)}).`,
    );
    return out;
  }
  out.push(
    `Snell-Descartes : n₁ sin i₁ = n₂ sin i₂, donc sin i₂ = ${fr(n1, 2)} × sin ${fr(i1, 0)}° ÷ ${fr(n2, 2)} ≈ ${fr(Math.sin(i2 * RAD), 3)} et i₂ ≈ ${deg(i2)}.`,
  );
  if (n2 > n1)
    out.push('Milieu 2 plus réfringent (n₂ > n₁) : le rayon se rapproche de la normale.');
  else if (n2 < n1)
    out.push(
      `n₂ < n₁ : le rayon s’écarte de la normale${lim !== null ? ` ; au-delà de ${deg(lim)}, réflexion totale` : ''}.`,
    );
  else out.push('Même indice : le rayon n’est pas dévié.');
  return out;
}

export function lensLines(input: LensInput, rank: number | null): string[] {
  const im = lensImage(input);
  const out: string[] = [];
  if (im.kind === 'infinite') {
    out.push(
      'Objet dans le plan focal objet (en F) : les rayons ressortent parallèles, l’image est rejetée à l’infini.',
    );
    return out;
  }
  out.push(
    im.kind === 'real'
      ? `Image réelle (on peut la recueillir sur un écran), ${im.gamma < 0 ? 'renversée' : 'droite'}, ${Math.abs(im.gamma) > 1 ? 'plus grande' : 'plus petite'} que l’objet.`
      : 'Objet entre F et O : les rayons ressortent divergents ; leurs prolongements donnent une image virtuelle, droite et agrandie (effet loupe).',
  );
  out.push(
    `γ = A’B’ ÷ AB = OA’ ÷ OA = ${signed(im.OA)} ÷ (${signed(input.OA)}) ≈ ${sigFixed(im.gamma, 2)}${im.gamma < 0 ? ' : négatif, image renversée' : ' : positif, image droite'}.`,
  );
  if (rank === null || rank >= 6)
    out.push(
      `Conjugaison : 1/OA’ − 1/OA = 1/f’ → 1/OA’ = 1/${fr(input.f, 0)} + 1/(${signed(input.OA)}) donne OA’ ≈ ${signed(im.OA)} cm. Objet réel : OA < 0.`,
    );
  return out;
}

export function telescopeLines(input: TelescopeInput): string[] {
  const t = telescope(input);
  return [
    `Lunette afocale : le foyer image F’₁ de l’objectif est confondu avec le foyer objet F₂ de l’oculaire ; longueur f’₁ + f’₂ = ${fr(t.length, 0)} mm.`,
    `Image intermédiaire A₁B₁ dans le plan focal de l’objectif : A₁B₁ ≈ f’₁ × α = ${fr(t.A1B1, 1)} mm.`,
    `Grossissement G = α’ ÷ α = f’₁ ÷ f’₂ = ${fr(input.f1, 0)} ÷ ${fr(input.f2, 0)} = ${fr(t.G, 1)} ; les rayons émergents sont parallèles à (B₁O₂), de l’autre côté de l’axe : image renversée.`,
  ];
}

/* ---------- défi ---------- */

export type QuizKind =
  | 'shadow-size'
  | 'shadow-types'
  | 'see'
  | 'source'
  | 'straight'
  | 'snell'
  | 'index'
  | 'image-2f'
  | 'gamma'
  | 'virtual'
  | 'conjugation'
  | 'focal'
  | 'nature'
  | 'G'
  | 'alpha-prime'
  | 'A1B1'
  | 'afocal';

export type Quiz = Mcq<QuizKind>;

export function quizKinds(rank: number | null): QuizKind[] {
  if (rank !== null && rank <= 4)
    return ['straight', 'source', 'see', 'shadow-types', 'shadow-size'];
  if (rank === 5) return ['index', 'snell', 'image-2f', 'gamma', 'virtual'];
  if (rank === 6) return ['conjugation', 'gamma', 'nature', 'focal', 'virtual'];
  if (rank === 7) return ['G', 'alpha-prime', 'A1B1', 'afocal', 'conjugation'];
  return ['shadow-size', 'snell', 'gamma', 'conjugation', 'G'];
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

type W = [string, string];
const q = (
  kind: QuizKind,
  question: string,
  good: string,
  wrongs: W[],
  explain: string,
  rng: Rng,
): Quiz => ({
  kind,
  question,
  options: mcqOptions(good, wrongs, rng),
  explain,
});
const u = (v: string, unit: string) => `${v}${NB}${unit}`;
const n2 = (v: number) => sigFixed(v, 2);

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  switch (kind) {
    case 'straight':
      return q(
        kind,
        'Dans une pièce enfumée, on voit le faisceau d’un laser. Il est…',
        'Rectiligne : la lumière se propage en ligne droite',
        [
          [
            'Courbe, il retombe vers le sol',
            'Dans un milieu transparent et homogène, la lumière ne retombe pas : elle va en ligne droite.',
          ],
          [
            'Invisible sans fumée parce qu’il n’existe plus',
            'Sans fumée, il existe toujours ; on ne le voit pas car rien ne diffuse sa lumière vers l’œil.',
          ],
          ['Ondulé, comme une vague', 'Le rayon lumineux se modélise par une droite.'],
        ],
        'Dans un milieu transparent et homogène (air, eau, vide), la lumière se propage en ligne droite ; la fumée diffuse un peu de lumière vers l’œil.',
        rng,
      );
    case 'source': {
      const [obj, good] = pick(
        [
          ['La Lune', 'Un objet diffusant'],
          ['Le Soleil', 'Une source primaire'],
          ['Une lampe de poche allumée', 'Une source primaire'],
          ['Un livre éclairé', 'Un objet diffusant'],
        ] as const,
        rng,
      );
      return q(
        kind,
        `${obj} est…`,
        good,
        [
          ['Une source primaire', 'Une source primaire PRODUIT sa lumière.'],
          ['Un objet diffusant', 'Un objet diffusant renvoie la lumière qu’il reçoit.'],
          [
            'Un objet opaque qui ne renvoie pas de lumière',
            'S’il ne renvoyait rien, on ne le verrait pas.',
          ],
          ['Un objet transparent', 'Il ne laisse pas passer la lumière à travers lui.'],
        ].filter(([t]) => t !== good) as W[],
        'Une source primaire produit sa propre lumière ; un objet diffusant renvoie dans toutes les directions une partie de la lumière qu’il reçoit (la Lune diffuse la lumière du Soleil).',
        rng,
      );
    }
    case 'see':
      return q(
        kind,
        'Pourquoi vois-tu un livre posé sur ta table ?',
        'De la lumière diffusée par le livre entre dans ton œil',
        [
          [
            'Ton œil envoie des rayons vers le livre',
            'L’œil n’envoie rien : la lumière va de l’objet vers l’œil.',
          ],
          [
            'Le livre produit sa propre lumière',
            'Un livre n’est pas une source primaire : il diffuse la lumière qu’il reçoit.',
          ],
          [
            'Il suffit qu’il soit devant toi, même dans le noir',
            'Dans le noir complet, aucune lumière n’arrive du livre : on ne le voit pas.',
          ],
        ],
        'On voit un objet quand de la lumière venant de cet objet entre dans l’œil ; un objet diffusant doit donc être éclairé.',
        rng,
      );
    case 'shadow-types':
      return q(
        kind,
        'Une lampe éclaire un ballon posé devant un mur. La zone sombre sur le mur s’appelle…',
        'L’ombre portée',
        [
          ['L’ombre propre', 'L’ombre propre est sur le ballon lui-même : sa face non éclairée.'],
          [
            'La pénombre',
            'La pénombre n’existe qu’avec une source étendue ; c’est une zone partiellement éclairée.',
          ],
          ['Le reflet', 'Un mur n’est pas un miroir : la zone sombre est une ombre.'],
        ],
        'Ombre propre : partie non éclairée de l’objet ; ombre portée : zone sombre que l’objet crée sur l’écran (le mur).',
        rng,
      );
    case 'shadow-size': {
      const h = pick([2, 3, 4, 5], rng);
      const d1 = pick([20, 25, 40], rng);
      const k = pick([2, 3, 4], rng);
      const d2 = d1 * k;
      return q(
        kind,
        `Une lampe ponctuelle éclaire un objet de ${h} cm placé à ${d1} cm d’elle ; l’écran est à ${d2} cm de la lampe. Taille de l’ombre portée ?`,
        u(fr(h * k, 1), 'cm'),
        [
          [
            u(fr(h, 1), 'cm'),
            'Avec une petite source, l’ombre portée est plus grande que l’objet.',
          ],
          [
            u(fr(h * (k - 1), 1), 'cm'),
            `Les distances se comptent depuis la lampe : ${d2} ÷ ${d1} = ${k}.`,
          ],
          [
            u(fr(h / k, 2), 'cm'),
            'L’écran est plus loin que l’objet : l’ombre est agrandie, pas réduite.',
          ],
        ],
        `Thalès : H = h × d₂ ÷ d₁ = ${h} × ${d2} ÷ ${d1} = ${fr(h * k, 1)} cm.`,
        rng,
      );
    }
    case 'index': {
      const [m, n] = pick(
        [
          ['l’eau', 1.33],
          ['le verre', 1.5],
          ['le diamant', 2.42],
        ] as const,
        rng,
      );
      const v = 3e8 / n;
      const vtxt = `${n2(v / 1e8)} × 10⁸ m/s`;
      return q(
        kind,
        `Dans ${m}, la lumière se propage à ${vtxt} (c = 3,00 × 10⁸ m/s). Indice de ${m} ?`,
        fr(n, 2),
        [
          [fr(1 / n, 2), 'n = c ÷ v, pas v ÷ c : n est toujours supérieur à 1.'],
          [fr(n * 3, 2), 'n = c ÷ v, sans unité.'],
          ['1', 'La lumière est plus lente dans ce milieu que dans le vide : n > 1.'],
        ],
        `n = c ÷ v = 3,00 × 10⁸ ÷ (${vtxt.replace(' m/s', '')}) ≈ ${fr(n, 2)} (sans unité).`,
        rng,
      );
    }
    case 'snell': {
      const i1 = pick([30, 40, 45, 60], rng);
      const [lab, n2v] = pick(
        [
          ['l’eau (n = 1,33)', 1.33],
          ['du verre (n = 1,50)', 1.5],
        ] as const,
        rng,
      );
      const i2 = refract(1, n2v, i1)!;
      return q(
        kind,
        `Un rayon passe de l’air (n = 1,00) dans ${lab} avec un angle d’incidence de ${i1}°. Angle de réfraction ?`,
        `${fr(i2, 0)}°`,
        [
          [`${i1}°`, 'Le rayon change de direction en entrant dans un milieu plus réfringent.'],
          [
            `${fr(Math.min(89, i1 * n2v), 0)}°`,
            'On applique la loi aux SINUS, pas aux angles ; et le rayon se rapproche de la normale.',
          ],
          [`${fr(i1 / n2v, 0)}°`, 'On divise sin i₁ par n₂, pas l’angle lui-même.'],
        ],
        `n₁ sin i₁ = n₂ sin i₂ : sin i₂ = sin ${i1}° ÷ ${fr(n2v, 2)} ≈ ${fr(Math.sin(i2 * RAD), 3)}, i₂ ≈ ${fr(i2, 0)}° (le rayon se rapproche de la normale).`,
        rng,
      );
    }
    case 'image-2f':
      return q(
        kind,
        'Un objet est placé à 2f’ devant une lentille convergente. Son image est…',
        'Réelle, renversée, de même taille, à 2f’ derrière',
        [
          ['Virtuelle, droite et agrandie', 'C’est le cas d’un objet entre F et O (effet loupe).'],
          [
            'Réelle, droite, deux fois plus grande',
            'Une image réelle donnée par une lentille convergente est renversée.',
          ],
          ['À l’infini', 'C’est le cas d’un objet placé au foyer objet F.'],
        ],
        'Rayon parallèle → passe par F’ ; rayon par O → non dévié : ils se coupent à 2f’ derrière la lentille, γ = −1.',
        rng,
      );
    case 'gamma': {
      const AB = pick([1, 2, 4], rng);
      const g = pick([-3, -2, -0.5, 2.5], rng);
      const ApBp = g * AB;
      return q(
        kind,
        `Un objet AB de ${fr(AB, 1)} cm donne une image ${ApBp < 0 ? 'renversée' : 'droite'} de ${fr(Math.abs(ApBp), 1)} cm. Grandissement γ ?`,
        fr(g, 1),
        [
          [
            fr(-g, 1),
            ApBp < 0
              ? 'Image renversée : A’B’ est négatif, donc γ aussi.'
              : 'Image droite : γ est positif.',
          ],
          [fr(1 / g, 2), 'γ = A’B’ ÷ AB : image divisée par objet, pas l’inverse.'],
          [fr(Math.abs(ApBp) + AB, 1), 'γ est un quotient, pas une somme.'],
        ],
        `γ = A’B’ ÷ AB = ${fr(ApBp, 1)} ÷ ${fr(AB, 1)} = ${fr(g, 1)} : ${g < 0 ? 'image renversée' : 'image droite'}, ${Math.abs(g) > 1 ? 'plus grande' : 'plus petite'} que l’objet.`,
        rng,
      );
    }
    case 'virtual':
      return q(
        kind,
        'On place un objet entre le foyer objet F et une loupe (lentille convergente). On observe…',
        'Une image virtuelle, droite et agrandie',
        [
          [
            'Une image réelle sur un écran, renversée',
            'Les rayons ressortent divergents : aucune image ne se forme sur un écran.',
          ],
          [
            'Aucune image',
            'L’œil voit une image virtuelle, à l’endroit des prolongements des rayons.',
          ],
          [
            'Une image réelle, droite et plus petite',
            'Avec l’objet entre F et O, l’image est virtuelle et agrandie.',
          ],
        ],
        'Objet entre F et O : les rayons émergents divergent ; leurs prolongements se coupent avant la lentille : image virtuelle, droite, agrandie (effet loupe).',
        rng,
      );
    case 'conjugation': {
      const f = pick([5, 10, 20], rng);
      const k = pick([1.5, 2, 3, 4], rng);
      const OA = -k * f;
      const OAp = 1 / (1 / f + 1 / OA);
      return q(
        kind,
        `Lentille convergente de distance focale f’ = ${f} cm ; objet réel à OA = ${fr(OA, 1)} cm. Position de l’image OA’ ?`,
        u(n2(OAp), 'cm'),
        [
          [
            u(n2(1 / (1 / f - 1 / OA)), 'cm'),
            'Objet réel : OA < 0. 1/OA’ = 1/f’ + 1/OA, avec le signe de OA.',
          ],
          [u(n2(-OAp), 'cm'), 'L’image d’un objet placé au-delà de F est réelle : OA’ > 0.'],
          [u(n2(f + OA), 'cm'), 'On additionne des INVERSES : 1/OA’ = 1/f’ + 1/OA.'],
        ],
        `1/OA’ = 1/f’ + 1/OA = 1/${f} + 1/(${fr(OA, 1)}) donne OA’ ≈ ${n2(OAp)} cm.`,
        rng,
      );
    }
    case 'focal': {
      const OA = pick([-30, -20, -15], rng);
      const OAp = pick([15, 20, 30, 60], rng);
      const f = 1 / (1 / OAp - 1 / OA);
      return q(
        kind,
        `Un objet réel à OA = ${OA} cm donne une image nette sur un écran à OA’ = ${OAp} cm. Distance focale de la lentille ?`,
        u(n2(f), 'cm'),
        [
          [
            u(n2(1 / (1 / OAp + 1 / OA)), 'cm'),
            '1/f’ = 1/OA’ − 1/OA : attention au signe moins ET au signe de OA.',
          ],
          [
            u(n2(OAp - OA), 'cm'),
            'On ne soustrait pas les distances : ce sont les inverses qui se combinent.',
          ],
          [u(n2(OAp / 2), 'cm'), 'f’ = OA’ / 2 seulement si l’objet est à 2f’ (OA = −OA’).'],
        ],
        `1/f’ = 1/OA’ − 1/OA = 1/${OAp} − 1/(${OA}) donne f’ ≈ ${n2(f)} cm.`,
        rng,
      );
    }
    case 'nature': {
      const g = pick([-2, -0.5, 3], rng);
      const good =
        g < 0
          ? g < -1
            ? 'Renversée et plus grande'
            : 'Renversée et plus petite'
          : 'Droite et plus grande';
      return q(
        kind,
        `Le grandissement vaut γ = ${fr(g, 1)}. L’image est…`,
        good,
        [
          ['Renversée et plus grande', 'γ < 0 : renversée ; |γ| > 1 : plus grande.'],
          ['Renversée et plus petite', 'γ < 0 : renversée ; |γ| < 1 : plus petite.'],
          ['Droite et plus grande', 'γ > 0 : droite ; |γ| > 1 : plus grande.'],
          ['Droite et plus petite', 'γ > 0 : droite ; |γ| < 1 : plus petite.'],
        ].filter(([t]) => t !== good) as W[],
        'Signe de γ : le sens de l’image (négatif : renversée) ; |γ| : sa taille (supérieur à 1 : agrandie).',
        rng,
      );
    }
    case 'G': {
      const f1 = pick([600, 900, 1000, 1200], rng);
      const f2 = pick([10, 20, 25], rng);
      return q(
        kind,
        `Lunette afocale : objectif f’₁ = ${f1} mm, oculaire f’₂ = ${f2} mm. Grossissement ?`,
        fr(f1 / f2, 1),
        [
          [fr(f2 / f1, 3), 'G = f’₁ ÷ f’₂ : la grande focale (objectif) au numérateur.'],
          [fr(f1 * f2, 0), 'G est un quotient de focales, pas un produit.'],
          [
            fr(f1 - f2, 0),
            'f’₁ − f’₂ n’a pas de sens ici ; la longueur de la lunette est f’₁ + f’₂.',
          ],
        ],
        `G = α’ ÷ α = f’₁ ÷ f’₂ = ${f1} ÷ ${f2} = ${fr(f1 / f2, 1)} (sans unité).`,
        rng,
      );
    }
    case 'alpha-prime': {
      const G = pick([30, 36, 40, 50], rng);
      const a = pick([2, 4.5, 9], rng); // × 10⁻³ rad
      return q(
        kind,
        `La Lune est vue à l’œil nu sous α = ${fr(a, 1)} × 10⁻³ rad. À travers une lunette de grossissement G = ${G}, sous quel angle α’ ?`,
        u(n2(G * a * 1e-3), 'rad'),
        [
          [u(n2((a * 1e-3) / G), 'rad'), 'La lunette agrandit l’angle : α’ = G × α.'],
          [
            u(n2(G * a), 'rad'),
            'α vaut ' + fr(a, 1) + ' × 10⁻³ rad : n’oublie pas la puissance de dix.',
          ],
          [u(n2(G + a * 1e-3), 'rad'), 'α’ = G × α : on multiplie.'],
        ],
        `α’ = G × α = ${G} × ${fr(a, 1)} × 10⁻³ ≈ ${n2(G * a * 1e-3)} rad.`,
        rng,
      );
    }
    case 'A1B1': {
      const f1 = pick([500, 800, 1000], rng);
      const a = pick([2, 5, 9], rng); // × 10⁻³ rad
      const v = f1 * a * 1e-3;
      return q(
        kind,
        `Objectif de f’₁ = ${f1} mm ; astre vu sous α = ${a} × 10⁻³ rad. Taille de l’image intermédiaire A₁B₁ ?`,
        u(n2(v), 'mm'),
        [
          [u(n2(f1 / (a * 1e-3)), 'mm'), 'A₁B₁ ≈ f’₁ × α (petits angles, α en radians).'],
          [u(n2(v * 2), 'mm'), 'A₁B₁ = f’₁ × α, sans facteur 2.'],
          [u(n2(f1 * a), 'mm'), 'α = ' + a + ' × 10⁻³ rad : la puissance de dix compte.'],
        ],
        `L’image d’un astre (à l’infini) est dans le plan focal image : A₁B₁ ≈ f’₁ × α = ${f1} × ${a} × 10⁻³ = ${n2(v)} mm.`,
        rng,
      );
    }
    case 'afocal':
      return q(
        kind,
        'Dans une lunette astronomique afocale…',
        'Le foyer image de l’objectif est confondu avec le foyer objet de l’oculaire',
        [
          [
            'L’objectif a la plus petite distance focale',
            'L’objectif (côté astre) a la PLUS GRANDE distance focale.',
          ],
          [
            'L’image finale est droite',
            'L’image donnée par une lunette astronomique est renversée.',
          ],
          [
            'Les rayons émergents sont parallèles à l’axe',
            'Ils sont parallèles entre eux et à (B₁O₂), inclinés de α’ sur l’axe.',
          ],
        ],
        'Afocale : F’₁ = F₂ ; d’un objet à l’infini, elle donne une image à l’infini (rayons émergents parallèles), renversée, vue sous α’ = G α.',
        rng,
      );
  }
}
